import React, { ForwardRefRenderFunction, forwardRef } from "react";
//#region redux
import { ThunkDispatch } from "redux-thunk";
import { useDispatch, useSelector } from "react-redux";
import { addPaymentMethod, updatePaymentMethod } from "store/actions/authActions";
//#endregion
//#region types
import { AddPaymentMethodFormContainerProps } from "models/propTypes/receiptPropTypes";
import { ReceiptFormTypes } from "models/formTypes";
import { FormHandles } from "models/propTypes/genericFormProps";
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
//#endregion
//#region components
import AddPaymentMethodForm from "./addPaymentMethodForm";
import { useReceiptPaymentMethodService } from "serviceHooks";
//#endregion
import * as Yup from "yup";
import { LocalizationHelperInstance } from "utils/helpers";
import { AppState } from "store";

const AddPaymentMethodFormContainer: ForwardRefRenderFunction<FormHandles, AddPaymentMethodFormContainerProps> = (
  props,
  ref
) => {
  const receiptPaymentMethodService = useReceiptPaymentMethodService();
  const dispatch: ThunkDispatch<any, any, any> = useDispatch();
  const loggedInUserId = useSelector((store: AppState) => store.auth.loggedInUser.personId);

  const validationSchema = Yup.object<ReceiptFormTypes.AddPaymentMethod>({
    name: Yup.string().required(TranslationKeys.Validations.Required),
    paymentMethodType: Yup.string(),
    currencyMarkup: Yup.number()
      .max(5, TranslationKeys.Validations.CurrencyMarkUpMax)
      .min(0, TranslationKeys.Validations.CurrencyMarkUpMax),
    canDeactivate: Yup.boolean(),
    editMode: Yup.boolean().required().oneOf([true]),
  });

  const defaultValues: ReceiptFormTypes.AddPaymentMethod = props.id
    ? {
        paymentMethodType: LocalizationHelperInstance.getFormattedMessage(`Receipt.PaymentMethodType_${props.type}`),
        name: props.name ?? "",
        id: props.id,
        isActive: props.isActive,
        currencyMarkup: props.currencyMarkup,
        editMode: receiptPaymentMethodService.canEdit(props.type, props.canDeactivate),
      }
    : {
        paymentMethodType: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Receipt.OwnExpenses),
        name: "",
        isActive: true,
        editMode: true,
      };

  const handleSubmit = async (values: ReceiptFormTypes.AddPaymentMethod) => {
    const paymentMethod: WintSuperkollAPIModel.ReceiptPaymentMethod = {
      id: 0,
      name: values.name,
      type: WintSuperkollAPIModel.ReceiptPaymentMethodType.OwnExpense,
      currencyMarkup: values.currencyMarkup,
      isActive: values.isActive ?? true,
      personId: loggedInUserId,
      canDeactivate: false,
      isPaymentMethodTypeDisabled: false,
    };

    if (props.id) {
      paymentMethod.id = props.id;
      await receiptPaymentMethodService.update(props.id, {
        name: values.name,
        currencyMarkup: values.currencyMarkup,
        isActive: values.isActive,
      } as WintSuperkollAPIModel.ReceiptPaymentMethodPut);
      dispatch(updatePaymentMethod(paymentMethod));
    } else {
      const newId = await receiptPaymentMethodService.post({
        name: values.name,
        currencyMarkup: values.currencyMarkup,
        isActive: values.isActive,
      } as WintSuperkollAPIModel.ReceiptPaymentMethodPost);
      paymentMethod.id = newId;
      dispatch(addPaymentMethod(paymentMethod));
    }

    return props.submitResultFormatter ? props.submitResultFormatter(paymentMethod) : paymentMethod;
  };

  return (
    <AddPaymentMethodForm
      ref={ref}
      {...props}
      validationSchema={validationSchema}
      defaultValues={defaultValues}
      handleSubmit={handleSubmit}
    />
  );
};

export default forwardRef(AddPaymentMethodFormContainer);
